import React from "react";
import { Link } from "react-router-dom";
import { Lock, Crown, Check, Download } from "lucide-react";
import { useSub } from "@/lib/subscription";
import { isFreeGame } from "@/lib/gating";

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

// Small "Premium" pill for locked cards.
export function LockBadge({ className = "" }) {
  return (
    <span data-testid="lock-badge" className={`inline-flex items-center gap-1 bg-purple-900/85 text-yellow-300 text-[11px] font-bold px-2.5 py-1 rounded-full shadow ${className}`}>
      <Lock size={12} strokeWidth={3} /> Premium
    </span>
  );
}

// Full-screen friendly upgrade prompt for locked sections.
export function UpgradePrompt({ title, kind = "activity" }) {
  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-8" data-testid="upgrade-prompt">
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-10">
        <div className="ll-card p-8 sm:p-10 text-center bg-gradient-to-br from-violet-500 to-purple-600 text-white border-purple-400">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-white/20 flex items-center justify-center">
            <Lock size={40} className="text-yellow-300" strokeWidth={2.5} />
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl mt-5">{title ? `${title} is a Premium ${kind}` : "This is a Premium adventure"}</h1>
          <p className="text-white/90 font-semibold mt-3">Ask a grown-up to unlock all 10 learning categories, 50+ activities and every mini-game.</p>
          <ul className="text-left max-w-sm mx-auto mt-6 space-y-2.5">
            {["Every learning category & activity", "All mini-games", "Daily Adventures, badges & stickers"].map((f) => (
              <li key={f} className="flex items-center gap-2 font-semibold text-white/95">
                <span className="bg-yellow-300 text-purple-900 rounded-full p-0.5"><Check size={14} strokeWidth={3} /></span>{f}
              </li>
            ))}
          </ul>
          <Link to="/pricing" data-testid="upgrade-prompt-cta" className="ll-btn bg-yellow-300 text-purple-900 shadow-[0_6px_0_0_#CA8A04] mt-7 w-full sm:w-auto sm:px-10 text-lg mx-auto">
            <Crown size={20} /> See plans & unlock
          </Link>
          <div className="mt-4">
            <Link to="/learn" data-testid="upgrade-prompt-back" className="text-white/80 font-bold text-sm underline">Back to free activities</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

// Receipt shown after a successful payment (and reusable in Parent history).
export function ReceiptCard({ receipt, testId = "receipt-card" }) {
  if (!receipt) return null;
  return (
    <div className="ll-card p-5 bg-white border-emerald-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg text-purple-900">Payment receipt</div>
        <span className="text-2xl">🧾</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
        <div className="text-purple-500 font-bold">Plan</div>
        <div className="text-purple-900 font-bold text-right capitalize" data-testid="receipt-plan">{receipt.plan_name || receipt.plan}</div>
        <div className="text-purple-500 font-bold">Amount paid</div>
        <div className="text-purple-900 font-bold text-right" data-testid="receipt-amount">{inr(receipt.amount)}</div>
        <div className="text-purple-500 font-bold">Payment date</div>
        <div className="text-purple-900 font-bold text-right" data-testid="receipt-date">{fmtDate(receipt.paid_at)}</div>
        <div className="text-purple-500 font-bold">Access until</div>
        <div className="text-purple-900 font-bold text-right" data-testid="receipt-expiry">{fmtDate(receipt.ends_at)}</div>
        <div className="text-purple-500 font-bold">Receipt no.</div>
        <div className="text-purple-700 font-mono text-right text-xs" data-testid="receipt-id">{receipt.receipt_id}</div>
      </div>
    </div>
  );
}

export function ReceiptModal({ receipt, onClose }) {
  if (!receipt) return null;
  const printReceipt = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-purple-950/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="receipt-modal" onClick={onClose}>
      <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-3">
          <div className="inline-flex items-center gap-2 bg-emerald-400 text-emerald-950 font-display font-bold px-4 py-1.5 rounded-full shadow">🎉 Payment successful</div>
        </div>
        <ReceiptCard receipt={receipt} testId="receipt-modal-card" />
        <div className="flex gap-3 mt-4">
          <button data-testid="receipt-print-button" onClick={printReceipt} className="ll-btn ll-btn-secondary flex-1"><Download size={18} /> Save / print</button>
          <button data-testid="receipt-close-button" onClick={onClose} className="ll-btn ll-btn-primary flex-1">Start learning</button>
        </div>
      </div>
    </div>
  );
}


// Guards a game route: shows the upgrade prompt for locked games, else renders the game.
export function GameGate({ gameId, children }) {
  const { premium } = useSub();
  if (!premium && !isFreeGame(gameId)) return <UpgradePrompt title="This game" kind="game" />;
  return children;
}
